import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';

const autocompleteService = { current: null };
const placeService = { current: null };
const sessionToken = { current: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
}));

export default function LocationInput(props) {
  const classes = useStyles();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService?.current?.getPlacePredictions(request, callback);
      }, 200),
    [],
  );

  useEffect(() => {
    let active = true;

    if (window.google) {
      if (!autocompleteService?.current) {
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
      if (!placeService?.current) {
        placeService.current = new window.google.maps.places.PlacesService(
          document.getElementById('dummy_map'),
        );
      }
    }

    if (!autocompleteService?.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue, sessionToken: sessionToken?.current }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  useEffect(() => {
    if (value) {
      placeService?.current?.getDetails(
        {
          placeId: value.place_id,
          fields: ['address_components', 'vicinity', 'geometry.location'],
          sessionToken: sessionToken?.current,
        },
        function (res, status) {
          if (status === 'OK') {
            props.onLocationSelect(res);
          } else {
            console.error(status);
          }
        },
      );
    }
  }, [value]);

  return (
    <Autocomplete
      id='google-map-autocomplete'
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => <TextField {...params} label='Search Google Maps' fullWidth />}
      renderOption={(option) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        let parts;
        if (matches && matches.length > 0) {
          parts = parse(
            option.structured_formatting.main_text,
            matches?.map((match) => [match.offset, match.offset + match.length]),
          );
        }
        return (
          <Grid container alignItems='center'>
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts?.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}
              <Typography variant='body2' color='textSecondary'>
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
      noOptionsText='No result found'
    />
  );
}
